/**
 * English. The source of truth for every key in the system.
 *
 * Somali is typed as `typeof en`, so adding a key here and forgetting it there
 * fails the build rather than falling back silently. That is the whole reason
 * this is a typed object and not a JSON file behind a string lookup: the
 * failure worth catching is a half-translated screen reaching a pharmacy, and
 * only the compiler catches that before a person does.
 *
 * Values that take a number or a name are functions. Interpolation and plurals
 * are written by hand per key, which is tractable at two locales and removes a
 * dependency that would otherwise sit under every string in the app.
 */
const en = {
  /* ------------------------------------------------------------- general */
  appName: "FarmashiBook",
  updateTitle: "Update FarmashiBook",
  updateBody: "This version is too old to keep working. Update it from the Play Store to carry on. Nothing on this phone is lost.",
  updateButton: "Update",
  closeApp: "Close the app",

  /* deleting the pharmacy (Settings → Pharmacy account) */
  deletePharmacy: "Delete pharmacy account",
  deleteWhat: (name: string) => `Deleting ${name} removes, for everyone:`,
  deleteBranches: "Every branch",
  deleteStock: "Every medicine and its stock",
  deleteSales: "Every sale and receipt",
  deleteCustomers: "Every customer and what they owe",
  deleteStaff: "Every staff login",
  deleteSignsOut: "Every phone is signed out at once.",
  deleteAfterDays: (phone: string) =>
    `After 30 days it is erased for good. Until then, call support on ${phone} to bring it back.`,
  deletePassword: "Your password, to confirm",
  deleteForever: "Delete forever",
  deletedTitle: (name: string) => `${name} is deleted`,
  deletedBody: (date: string, phone: string) =>
    `It is erased for good on ${date}. Until then, call support on ${phone} to bring it back.`,
  cancel: "Cancel",
  close: "Close",
  back: "Back",
  next: "Next",
  save: "Save",
  done: "Done",
  retry: "Try again",
  loading: "Loading…",
  offline: "No connection",
  offlineWorking: "Working offline. Changes will sync when you reconnect.",

  /* the web version's shared pieces (buttons, fields, lists) */
  showPassword: "Show password",
  hidePassword: "Hide password",
  previous: "Previous",
  loadMore: "Load more",
  clearSearch: "Clear the search",
  /* the web version's frame: menu, top bar */
  billing: "Billing",
  more: "More",
  signOutConfirm: "Sign out?",
  /* the web version's sign-in footer */
  help: "Help",
  privacyPolicy: "Privacy policy",
  /* the web version's welcome and sign-in pages (computer layout) */
  webTagline: "Your pharmacy's stock, sales and credit, on the computer and the phone.",
  /* The web's own words where the phone says "Tap" (a computer is clicked, a phone tapped). */
  webChooseDate: "Choose a date",
  webPickToAddStock: "Choose a medicine to add its stock",
  webSelectToChange: "Select the customer or a medicine to change it.",
  webNoSalesYet: "No sales yet. Use Add sale to record the first one.",
  webFeatureStock: "Medicines, stock and expiry dates",
  webFeatureSales: "Sales, with receipts on WhatsApp",
  webFeatureCredit: "Credit (deyn): who owes, and how much",
  webFeatureReports: "Reports for every branch",
  whoIsSigningIn: "Who is signing in?",
  /* the web version's tables */
  price: "Price",
  dateTime: "Date and time",
  /* a page that could not load (web): usually a new version was published */
  appUpdated: "FarmashiBook has been updated. Reload the page to carry on.",
  pageFailed: "This page could not open.",
  reload: "Reload",
  /* leaving a form with changes not saved (web) */
  discardChanges: "Discard changes?",
  discardChangesBody: "What you typed here is not saved.",
  discard: "Discard",

  /* ------------------------------------------------------------- dates */
  chooseDate: "Tap to choose a date",
  /** On a small chip: one day of a list. */
  pickDay: "Choose date",
  today: "Today",
  monthNames: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
  /** Monday first. */
  weekdaysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],

  /* ---------------------------------------------------------------- auth */
  signIn: "Sign in",
  signOut: "Sign out",
  signInAsOwner: "Admin",
  signInAsStaff: "Staff",
  phone: "Phone number",
  password: "Password",
  username: "Username",
  currentPassword: "Current password",
  newPassword: "New password",
  changePassword: "Change password",
  forgotPassword: "I have forgotten my password",
  verificationCode: "Code",
  sendCode: "Send code",
  codeSentTo: (masked: string) => `We sent a code to ${masked}.`,
  codeExpiresIn: (seconds: number) =>
    seconds >= 60
      ? `The code lasts ${Math.round(seconds / 60)} minute${Math.round(seconds / 60) === 1 ? "" : "s"}.`
      : `The code lasts ${seconds} seconds.`,

  /* -------------------------------------------------------------- signup */
  welcomeTagline: "Run your pharmacy from your phone",
  signUp: "Sign up",
  signInOwnerIntro: "Use the phone number or email you registered with.",
  signInStaffIntro: "Use the username and password the owner gave you.",
  enterYourPhone: "Phone number",
  enterYourUsername: "Enter your username",
  noAccountSignUp: "No account yet? Sign up",
  haveAccountSignIn: "Already have an account? Sign in",
  phoneOrEmail: "Phone number or email",
  enterPhoneOrEmail: "Phone number or email",
  email: "Email",
  enterYourEmail: "Email address",
  signUpWithPhone: "Sign up with a phone number instead",
  signUpWithEmail: "Sign up with an email instead",
  signupEmailIntro: "We will send a code to this email to make sure it is yours.",
  signupEmailHint: "This becomes your sign-in, and the way to recover your password.",
  checkSpam: "Not there? Look in your Spam folder.",
  pharmacyPhoneHint: "So the shop can be reached. To sign in with it too, confirm it later in Profile.",
  theirPhoneOrEmail: "Their phone number or email",
  waysToSignIn: "How you sign in",
  notAdded: "Not added",
  addContact: "Add",
  addEmail: "Add your email",
  changeEmail: "Change your email",
  addPhone: "Add your phone number",
  changePhone: "Change your phone number",
  contactIntro: "We will send a code to it. Once you type the code here, you can sign in with it and recover your password with it.",
  contactSaved: "Saved. You can now sign in with it.",
  signInWaysNote: "Add both, so losing one never locks you out.",
  backToSignIn: "Back to sign in",
  sendCodeAgain: "Send the code again",
  chooseNewPassword: "Choose a new password",
  createPharmacy: "Set up your pharmacy",
  pharmacyName: "Pharmacy name",
  branchName: "Branch name",
  ownerName: "Your name",
  signupPhoneIntro: "We will send a code to this number to make sure it is yours.",
  signupPhoneHint: "This becomes your sign-in, and the only way to recover your password.",
  signupDetailsIntro: "Last step. You can change any of this later.",
  ownerPasswordHint: "At least 8 characters.",
  forgotPasswordIntro: "Enter the phone number or email you sign in with, and we will send you a code.",
  staffNoRecovery: "Forgotten your password? Ask the owner to reset it.",

  /* ----------------------------------------------------------- the shell */
  home: "Home",
  account: "Account",
  language: "Language",

  /* ------------------------------------- settings, profile, pharmacy account */
  profile: "Profile",
  pharmacyAccount: "Pharmacy account",
  settingsYou: "You",
  settingsPharmacy: "Pharmacy",
  settingsBranch: "Branch",
  appearance: "Appearance",
  themeLight: "Light",
  themeDark: "Dark",
  helpSupport: "Help and support",
  helpSupportHint: "A question, a problem, or something not working? Contact us.",
  callUs: "Call us",
  whatsAppUs: "WhatsApp us",
  emailUs: "Email us",
  openFailed: "This phone could not open that.",
  about: "About",
  termsOfUse: "Terms of use",
  termsUpdated: (date: string) => `Last updated ${date}`,
  appVersion: "App version",
  whatYouCanDo: "What you can do",
  phoneLetter: "This phone's receipt letter",
  pharmacyPhone: "Pharmacy phone",
  currency: "Currency",
  timeZone: "Time zone",
  plan: "Plan",
  planNames: { basic: "Basic", pro: "Pro" },
  staffInUse: "Staff",
  ofLimit: (n: number, limit: number | null) => (limit === null ? `${n}` : `${n} of ${limit}`),
  /* Plan (Phase 6). No prices and no way to pay on the phone: Google Play's rules. */
  ok: "OK",
  planFreeForNow: "Free for now",
  planFree: "Free",
  planFreeUntil: (day: string) => `Free until ${day}`,
  planPaidUntil: (day: string) => `Paid until ${day}`,
  planLine: (plan: string, standing: string) => `${plan} · ${standing}`,
  planEndingTitle: "Plan ending soon",
  planEndingBody: (daysLeft: number, day: string) =>
    daysLeft <= 0
      ? `Your plan ends today, ${day}.`
      : daysLeft === 1
        ? `Your plan ends tomorrow, ${day}.`
        : `Your plan ends in ${daysLeft} days, on ${day}.`,
  planEndedTitle: "Plan ended",
  planGraceOwner: (ended: string, until: string) =>
    `Your plan ended on ${ended}. Everything still works until ${until}. After that the app becomes view only.`,
  planGraceStaff: "The pharmacy's plan has ended. Please tell the owner.",
  viewOnly: "View only",
  viewOnlyOwner: (from: string) =>
    `View only since ${from}. You can look, share, and record credit paid back. Selling and stock changes are paused until the plan is renewed.`,
  viewOnlyStaff: "View only. Ask the owner to renew the plan.",
  alreadyPaid: "Already paid? Connect to the internet to update.",
  viewOnlyLine: "View only · the plan has ended",
  viewOnlyStopOwner: "The plan has ended. You can look and share, but nothing can be changed until the plan is renewed.",
  viewOnlyStopStaff: "The plan has ended. Ask the owner to renew.",
  /* Billing page (web only: the phone shows no prices or way to pay). */
  seePlans: "See plans",
  daysLeft: (n: number) => (n === 1 ? "1 day left" : `${n} days left`),
  graceUntil: (day: string) => `Ended. Everything still works until ${day}.`,
  viewOnlySince: (day: string) => `View only since ${day}.`,
  choosePlan: "Choose a plan",
  planBranches: (n: number) => (n === 1 ? "1 branch" : `Up to ${n} branches`),
  planStaff: (n: number | null) => (n === null ? "Staff without limit" : `Up to ${n} staff`),
  planSameRest: "Everything else is the same: selling, stock, credit, reports and transfers.",
  yourPlan: "Your plan",
  forMonths: (n: number) => (n === 1 ? "1 month" : `${n} months`),
  perMonthPrice: (amount: string) => `${amount} a month`,
  youSave: (amount: string) => `You save ${amount}`,
  howToPay: "How to pay",
  payWith: "Pay with",
  amountToPay: "Amount",
  dialThisCode: "Dial this code",
  scanToDial: "Scan with your phone's camera: the dialler opens with the code ready.",
  dialNow: "Dial now",
  payStep1: "Dial the code and confirm with your PIN.",
  payStep2: "Send your pharmacy's name on WhatsApp.",
  payStep3: "Your plan is renewed when we confirm the payment.",
  sendOnWhatsApp: "Send on WhatsApp",
  paymentMessage: (pharmacy: string, plan: string, period: string) => `Payment for ${pharmacy}, ${plan}, ${period}`,
  planChangeNote: "Changing from Basic to Pro takes effect when the payment is confirmed. More than 5 branches? Contact us.",
  planAllowsBranches: (n: number) => `Your plan allows ${n} branch${n === 1 ? "" : "es"}.`,
  planAllowsStaff: (n: number) => `Your plan allows ${n} staff.`,
  joined: "Joined",
  nameOnReceipts: "Printed at the top of every receipt, in every branch.",
  pharmacyNeedsInternet: "Pharmacy account needs internet.",
  role: "Role",
  roleOwner: "Owner",
  roleStaff: "Staff",
  or: "or",
  branch: "Branch",
  branchCount: (n: number) => `${n} branch${n === 1 ? "" : "es"}`,
  branches: "Branches",
  permissions: "Can reach",
  shellComingSoon: "The rest of the app arrives with products and the till.",

  menu: "Menu",
  sales: "Sales",
  reports: "Reports",
  settings: "Settings",
  comingSoon: "Coming soon",
  comingSoonBody: "This part of the app is still being built.",

  /* ------------------------------------------------------ staff management */
  staffMember: "Staff member",
  addStaffIntro: "They will sign in with a username and a password on their own phone.",
  theirName: "Their name",
  usernameHint: "Unique across every pharmacy, so pick from the suggestions if one is taken.",
  freeUsernames: "Free right now",
  defaultAccessNote: "They start with the till and read-only products. Change that once they exist.",
  staffAdded: "Added",
  passwordShownOnce: "This is the only time it is shown. If it is lost, reset it — it cannot be looked up.",
  noStaffYet: "Nobody yet. Add the first person.",
  deactivated: "deactivated",
  deactivate: "Deactivate",
  reactivate: "Reactivate",
  deactivatedNote: "They cannot sign in. Nothing has been deleted.",
  resetEndsSessions: "Every session they held has ended. A forgotten password and a stolen phone look the same from here.",
  /* Owners (2026-10-12): one main owner; the others can only leave. */
  mainOwner: "Main owner",
  invitationPending: "Invitation not accepted yet",
  makeMainOwner: "Make main owner",
  makeMainOwnerAsk: (name: string) => `Make ${name} the main owner?`,
  makeMainOwnerBody:
    "They will be able to deactivate other owners, including you. You stay an owner. Type your password to confirm.",
  deactivateOwnerAsk: (name: string) => `Deactivate ${name}?`,
  deactivateOwnerBody:
    "They are signed out of every phone at once and cannot sign in. Everything they recorded stays. You can activate them again later.",
  activateAgain: "Activate again",
  leavePharmacy: "Leave this pharmacy",
  leavePharmacyBody:
    "You will be signed out of every phone and cannot sign in again unless the main owner activates you. Everything you recorded stays. Type your password to confirm.",
  mainOwnerLeaveNote: "To leave, first make another owner the main owner.",
  onlyMainOwnerChanges: "Only the main owner can deactivate owners or hand the role on.",
  mainOwnerNote: "The main owner can deactivate other owners and hand this role to another owner.",
  adminAreasNote: "Staff, billing and the activity log are never grantable — they are yours alone, whatever is switched on above.",

  /* ------------------------------------------------------- co-owners */
  inviteOwner: "Invite an owner",
  inviteOwnerIntro: "A co-owner gets their own sign-in, their own password, and their own name on every action in the activity log.",
  theirPhone: "Their phone number",
  enterTheirPhone: "Enter their phone",
  theirPhoneHint: "The code goes there, so it has to be theirs.",
  coOwnerNote: "They will be able to do everything you can, including managing staff and billing. There is no way to invite somebody as a partial owner.",
  sendInvitation: "Send invitation",
  invitationSent: "Invitation sent",
  invitationSentNote: (name: string) => `${name} has been sent a code.`,
  invitationNextSteps: "They open the app, choose \u201cI have an invitation\u201d, and enter that code with a password of their own. Until they do, they cannot sign in.",
  haveInvitation: "I have an invitation",
  acceptInvitation: "Accept invitation",
  acceptInvitationIntro: "Enter the phone number or email the invitation was sent to, the code you received, and a password you choose yourself.",
  acceptInvitePhoneHint: "The one the owner invited.",
  choosePassword: "Choose a password",

  /* -------------------------------------------------------- own account */
  passwordChanged: "Password changed.",
  passwordMinimum: (n: number) => `At least ${n} characters.`,
  changeEndsOtherSessions: "Every other session ends. This one stays.",

  seeCostPrice: "See cost prices",
  seeCostPriceHint: "What each batch cost, and the average cost. Without it, a person can still record stock.",

  /** The nine grantable sections, named for a person rather than for the code. */
  sections: {
    pos: "Till",
    products: "Products",
    batches: "Batches and expiry",
    purchases: "Add products and stock",
    transfers: "Transfers",
    customers: "Customers and credit",
    invoices: "Invoices",
    reports: "Reports",
    settings: "Settings",
  },

  /* --------------------------------------------------------------- staff */
  staff: "Staff",
  addStaff: "Add staff",
  staffCount: (n: number) => `${n} ${n === 1 ? "person" : "people"}`,
  handOverPassword: "Give them this password. It is shown once.",
  resetPassword: "Reset password",
  resetPasswordAsk: (name: string) => `Reset ${name}'s password?`,
  resetPasswordAskBody:
    "Their old password stops working at once and they are signed out on every phone. You will get a new password to give them.",
  yesReset: "Yes, reset",
  newPasswordFor: (name: string) => `New password for ${name}`,

  /* ------------------------------------------------------------ products */
  products: "Products",
  searchProducts: "Search by name, Somali name or generic name",
  noProductsYet: "Nothing here yet. Add the first product.",
  noProductsFound: (q: string) => `Nothing matches "${q}".`,
  addProduct: "Add product",
  tapToAddStock: "Tap a medicine to add its stock",
  noMedicinesYet: "No medicines yet.",
  notInStore: (q: string) => `"${q}" is not in the store.`,
  addNewMedicineNamed: (q: string) => `Add "${q}" as a new medicine`,
  editDetails: "Edit details",
  archived: "Archived",
  archiveProduct: "Archive",
  archiveProductHint:
    "It stops appearing in search and at the till. Nothing already sold is lost. Bring it back from Settings → Archived medicines.",
  archivedMedicines: "Archived medicines",
  archivedMedicinesHint: "Bring back a medicine you archived",
  archivedListNote: "Medicines archived in this branch. Bring one back and it is in Products and at the till again, with the stock it had.",
  bringBack: "Bring back",
  broughtBack: (name: string) => `${name} is back in Products.`,
  noArchived: "No archived medicines in this branch.",
  archivedNeedsInternet: "Archived medicines need internet.",

  /* the medicine */
  medicine: "Medicine",
  productName: "Name",
  productNameHint: "As printed on the box.",
  countedIn: "Counted in",
  countedInHint: "The smallest amount you ever sell. All stock is counted in this.",
  countedInOptions: ["tablet", "capsule", "piece", "ml", "sachet", "bottle", "tube", "vial", "ampoule"],
  /** The packs a medicine comes in, smallest to biggest: the order they nest in. */
  packOptions: ["strip", "sachet", "packet", "bottle", "box", "carton"],
  itComesIn: "It comes in",
  itComesInHint: (unit: string) => `Tap each pack it comes in. None, if it only comes as a ${unit}.`,
  packOther: "Other",
  packOtherLabel: "Name of the pack",
  packInside: (inner: string, pack: string) => `${inner} in 1 ${pack}`,
  packEquals: (pack: string, n: number, base: string) => `1 ${pack} = ${n} ${base}`,
  howManyHave: "How many do you have",
  howManyAdded: (pack: string) =>
    `The numbers are added together, and the total is worked out for you. If it all came as ${pack}, type only that.`,
  sellingPrice: "Selling price",
  countedInOther: "Something else",
  moreDetails: "More details",
  fewerDetails: "Fewer details",
  productNameSo: "Somali name",
  productNameSoHint: "What customers ask for. Used for searching.",
  genericName: "Generic name",
  genericNameHint: "The medicine inside it, such as paracetamol.",
  strength: "Strength",
  strengthHint: "Such as 500mg",
  form: "Form",
  formHint: "Such as tablet, syrup, injection",
  manufacturer: "Manufacturer",
  originCountry: "Country",
  category: "Category",
  barcode: "Barcode",
  barcodeHint: "Leave empty if the box has none.",
  isRx: "Prescription only",
  noExpiry: "Has no expiry date",
  fromMedicineList: "From the medicine list",
  noExpiryHint: "For soap, razors and similar. Medicines always have one.",
  reorderLevel: "Warn me when stock falls below",
  reorderLevelHint: (unit: string) => `Counted in ${unit}.`,

  /* this stock */
  thisStock: "This stock",
  size: "Size",
  newSize: "New size",
  sizeLabel: "Called",
  sizeLabelHint: "Such as box, strip, carton",
  sizeCount: (unit: string) => `How many ${unit} inside?`,
  howMany: "How many",
  costEach: (size: string) => `Cost per ${size}`,
  priceEach: (size: string) => `Selling price per ${size}`,
  priceEachHint:
    "Change it only if the price is going up or down. It applies to every one of this size.",
  expiryDate: "Expiry date",
  lotNumber: "Lot number",
  lotNumberHint: "Printed on the box. Leave empty if there is none.",
  supplier: "Supplier",
  noSupplier: "None",
  orTypeNew: "Or type a new one",
  dateReceived: "Date received",
  totalPaid: (amount: string) => `Total paid: ${amount}`,
  confirmExpired: "This stock has already expired",
  confirmExpiredBody:
    "Record it anyway? It will be kept apart from other stock, and the till will warn before selling it.",
  recordAnyway: "Record it",
  lotProblem: {
    expiry_required: "This is a medicine. Enter the expiry date.",
    expiry_in_past: "Already expired.",
    qty_not_positive: "How many?",
    qty_not_whole: "Enter a whole number.",
    cost_negative: "A cost cannot be negative.",
    no_lots: "Enter the stock.",
  },

  /* one product */
  stock: "Stock",
  inStore: "In store",
  batchCount: (n: number) => `${n} ${n === 1 ? "batch" : "batches"}`,
  avgCost: "Average cost",
  nearestExpiry: "Nearest expiry",
  expired: "Expired",
  expiresSoon: "Expires soon",
  sizesAndPrices: "Sizes and prices",
  holds: (qty: string) => `Holds ${qty}`,
  addSize: "Add a size",
  batches: "Batches",
  batchLeft: (left: string, received: string) => `${left} left of ${received}`,
  received: "Received",
  perUnit: (unit: string) => `per ${unit}`,
  cost: "Cost",
  correctCost: "Correct",
  costOfOne: (size: string) => `Cost of one ${size}`,
  correctCostHint: "Sales already made from this stock are corrected too, so profit comes out right.",
  batchStatus: {
    active: "In stock",
    quarantined: "Expired",
    written_off: "Written off",
  },

  /* changing a quantity */
  change: "Change",
  nowHave: (qty: string) => `Now: ${qty}`,
  newNumber: (unit: string) => `How many ${unit} are there now?`,
  equalsSize: (text: string) => `= ${text}`,
  why: "Why?",
  adjustReason: {
    expired: "Expired",
    damaged: "Damaged",
    missing: "Missing",
    returned: "Returned to supplier",
    counted_wrong: "Counted wrong",
    other: "Other",
  },
  messageOptional: "Message (optional)",
  messageRequired: "Message: say what happened",
  adjustProblem: {
    not_a_number: "Enter a whole number.",
    below_zero: "It cannot be less than zero.",
    unchanged: "That is the same number as now.",
    reason_cannot_raise: "Only Counted wrong or Other can put the number up.",
    note_required: "Say what happened.",
    invalid_receipt_no: "That receipt number does not belong to this phone.",
    receipt_taken: "Another sale already has that receipt number.",
  },
  changes: "Changes",
  changeBy: (who: string, date: string) => `${who} · ${date}`,
  ofBatch: (expiry: string) => `Batch ${expiry}`,

  /* expiry */
  expiry: "Expiry",
  expiryGroup: {
    expired: "Expired",
    month1: "Within 1 month",
    month2: "Within 2 months",
    month3: "Within 3 months",
  },
  medicineCount: (n: number) => `${n} ${n === 1 ? "medicine" : "medicines"}`,
  nothingExpiring: "Nothing has expired, and nothing expires in the next 3 months.",
  nothingInGroup: "Nothing here.",
  stockAtRisk: (amount: string) => `${amount} of stock`,
  expiresOn: (date: string) => `Expires ${date}`,
  expiredOn: (date: string) => `Expired ${date}`,
  qtyLeft: (qty: string) => `${qty} left`,

  /* ---------------------------------------------------------------- till */
  noneLeft: "None left",
  tillShort: (left: string, selling: string, after: string) =>
    `Out of stock: only ${left} left. Selling ${selling} leaves ${after}.`,
  tillShortNone: (selling: string, after: string) =>
    `Out of stock: none left. Selling ${selling} leaves ${after}.`,
  tillExpired: (date: string) => `Some of this stock expired on ${date}.`,
  soldWithoutStock: "Sold without stock",
  soldWithoutStockHint: "The next stock you record covers this first.",

  /* cart */
  total: "Total",
  chargedEach: (size: string) => `Price per ${size}`,
  normalPrice: (price: string) => `Normal price ${price}`,
  notAnAmount: "Use at most 10 numbers after the dot, like 0.005.",
  enterPrice: "Enter a price.",

  /* payment */
  nothingToPay: "This sale comes to nothing, so there is nothing to pay.",
  finishSale: "Finish sale",

  /* checking the sale before it is finished */
  checkSale: "Check the sale",
  tapToChange: "Tap the customer or a medicine to change it.",
  customer: "Customer",
  addCustomer: "+ Add customer",
  changeCustomer: "Change",
  paid: "Paid",
  sellOnCredit: "Sell on credit (deyn)",
  paidNow: "Paid now",
  /** Before the discount on the whole sale. */
  subtotal: "Subtotal",
  discount: "Discount",
  discountTooBig: "The discount cannot be more than the sale.",
  discountNegative: "Type the discount without a minus.",
  creditTooMuch: "On credit cannot be more than the total.",
  /* the receipt's products table */
  colNo: "No",
  colProduct: "Product",
  colQty: "Qty",
  colPrice: "Price",
  colAmount: "Amount",

  /* receipt */
  receipt: "Receipt",
  receiptNo: (n: string) => `Receipt ${n}`,
  shareToWhatsApp: "Share to WhatsApp",
  shareFailed: "It could not be shared. The receipt is still here to photograph.",
  newSale: "New sale",

  /* --------------------------------------------------------------- sales */
  addSale: "Add sale",
  continueSale: (total: string) => `Continue sale · ${total}`,
  continueEditing: (receiptNo: string) => `Continue editing receipt ${receiptNo}`,
  searchSales: "Name, phone, receipt or medicine",
  filterDate: "Date",
  allDates: "Show all dates",
  noSalesYet: "No sales yet. Tap Add sale to record the first one.",
  noSalesFound: "No sale matches.",
  edited: "Edited",
  cancelledTag: "Cancelled",

  /* one sale */
  soldBy: (name: string) => `sold by ${name}`,
  editSale: "Edit",
  editedBy: (name: string, when: string) => `Edited by ${name}, ${when}`,
  cancelledBy: (name: string, when: string) => `Cancelled by ${name}, ${when}`,
  whyWas: (reason: string) => `Why: ${reason}`,
  changeAdded: (name: string, what: string) => `${name}: added, ${what}`,
  changeRemoved: (name: string) => `${name}: removed`,
  changePricePart: (before: string, after: string) => `price ${before} \u2192 ${after}`,
  totalChange: (before: string, after: string) => `Total ${before} \u2192 ${after}`,
  cancelWindowOver: "A sale can only be cancelled for 14 days after it was made.",
  tooOldToEdit: "This sale is more than 30 days old. It can no longer be edited or cancelled.",
  unfinishedSale: "A sale is not finished",
  finishOtherSaleFirst: "Finish it or cancel it before editing another sale.",
  openUnfinished: "Open it",
  cancelSale: "Cancel sale",
  cancelSaleConfirm: "Are you sure you want to cancel this sale? Nothing has been recorded yet.",
  cancelRecordedConfirm: "Are you sure you want to cancel this sale? The medicines go back into stock.",
  yesCancelSale: "Yes, cancel it",
  keepSale: "No, keep it",
  cancelWhyHint: "The medicines go back into stock. The sale stays in the history, marked Cancelled.",
  editWhyHint: "The receipt keeps its number, and the sale shows what changed.",
  saleReason: {
    returned: "Customer returned it",
    wrong_medicine: "Wrong medicine",
    wrong_quantity: "Wrong quantity",
    wrong_price: "Wrong price",
    other: "Other",
  },

  /* add sale */
  editingSale: (receiptNo: string) => `Edit receipt ${receiptNo}`,
  customerName: "Customer name (optional)",
  customerPhone: "Customer phone (optional)",
  invalidPhone: "That is not a valid phone number.",
  pickMedicine: "Type or pick a medicine",
  mostSold: "Most sold",
  changeMedicine: "Change",
  changePrice: "Change price",
  useNormalPrice: "Use the normal price",
  removeMedicine: "Remove this medicine",
  addAnotherMedicine: "+ Add another medicine",
  newMedicine: "New medicine",
  soldAs: "Sold as",
  newMedicineHint: "Only what this sale needs. Record its stock, cost and expiry later in Products.",
  saveAndSell: "Save and sell",
  stopEditing: "Stop editing",
  stopEditingConfirm: "Stop editing? The sale stays as it was.",
  saveChanges: "Save changes",

  /* -------------------------------------------------- this phone's copy */
  syncFirstTime: "Getting the medicines onto this phone…",
  syncUpdating: "Updating…",
  syncUpToDate: (time: string) => `Up to date · ${time}`,
  syncOffline: (time: string | null) =>
    time ? `No internet · last update ${time}` : "No internet · nothing on this phone yet",
  syncFailing: (time: string | null) =>
    time ? `Can't reach the server · last update ${time}` : "Can't reach the server",
  clockWrong: (phone: string, real: string) =>
    `This phone's date or time is wrong. It shows ${phone}, but it is ${real}. Correct it in the phone's settings: receipts carry the phone's time.`,
  syncSending: (n: number) => `Sending ${n}…`,
  syncWaitingOffline: (n: number) => `${n} waiting to send · they go when the internet is back`,
  syncRefused: (n: number) => `${n} not accepted by the server · tap to see`,
  needFirstSync: "This phone needs the internet once to get its letter for receipt numbers. Connect, wait a moment, and try again.",
  waitingToSendNote: "Waiting to send. It is saved on this phone and goes to the server by itself when there is internet.",
  notAcceptedNote: (reason: string) => `The server did not accept this sale: ${reason}`,
  offlineProductNote: "No internet: this is the copy kept on this phone, for this branch. Changes are saved here and sent when there is internet.",
  savedOnPhone: "Saved on this phone. It will be sent by itself when there is internet.",
  notAcceptedTitle: "Not accepted",
  notAcceptedHint: "The server would not take these. Try again once the problem is fixed, or remove the change.",
  notAcceptedSale: (receiptNo: string) => `Sale ${receiptNo}: owners see it under Needs attention.`,
  notAcceptedCancel: (receiptNo: string) => `Cancel of receipt ${receiptNo}: owners see it under Needs attention.`,
  notAcceptedCredit: (name: string, receiptNo: string | null) =>
    receiptNo ? `Credit for ${name} · receipt ${receiptNo}` : `Credit for ${name}`,
  notAcceptedCreditChange: (name: string) => `Change to a credit for ${name}`,
  /* changing a credit added on the Credits page (2026-10-19) */
  changeCredit: "Change this credit",
  changeCreditHint: "Typed it wrong? Put the right amount and what it is for.",
  removeCredit: "Remove credit",
  removeCreditConfirm: "Remove this credit? It comes off what they owe.",
  keepCredit: "Keep it",
  creditChanged: "Credit changed.",
  wasAmount: (amount: string) => `Was ${amount}`,
  notAcceptedEdit: (receiptNo: string) => `Edit of receipt ${receiptNo}: owners see it under Needs attention.`,
  notAcceptedChange: (reason: string) => `This change was not accepted: ${reason} The owners see it under Needs attention.`,
  changedFirst:
    "Another phone changed this sale first, so this change was not made. The owners see it under Needs attention and decide.",
  stockChangeLine: {
    product: (name: string) => `${name}: added, with its first stock`,
    stock: (name: string) => `${name}: stock recorded`,
    adjust: (name: string, delta: string) => `${name}: quantity changed by ${delta}`,
    details: (name: string) => `${name}: details changed`,
    price: (name: string) => `${name}: price changed`,
    size: (name: string) => `${name}: size added`,
    archive: (name: string) => `${name}: archived`,
    cost: (name: string) => `${name}: cost corrected`,
  },
  removeChange: "Remove",
  nothingNotAccepted: "Nothing here: everything was accepted.",
  removeChangeConfirm: "Remove this change from the phone? It will not be sent.",
  offlineExpiryNote: "No internet: showing this branch, from the copy kept on this phone.",
  offlineSaleNote: "No internet: this is the copy kept on this phone. A cancel or an edit is saved here and sent when there is internet.",
  historyOffline: "No internet: showing the last 7 days kept on this phone.",
  waitingTag: "Waiting to send",
  notAcceptedTag: "Not accepted",
  cannotSignOutYet: (n: number) =>
    `${n} ${n === 1 ? "sale has" : "sales have"} not reached the server yet. Signing out would lose ${n === 1 ? "it" : "them"}. Connect to the internet and wait until the status line says everything is sent.`,

  thisPhone: (letter: string) => `This phone: ${letter}`,

  /* ----------------------------------------------------------- transfers */
  transfersTitle: "Transfers",
  status: "Status",
  toReceiveTab: (n: number) => `To receive (${n})`,
  sentTab: "Sent",
  receivedLately: "Received lately",
  nothingToReceive: "Nothing is on its way here.",
  nothingSent: "Nothing sent yet.",
  sendStock: "Send stock",
  sendTo: "Send to",
  addMedicineToSend: "Add a medicine",
  searchThisBranch: "Search this branch's medicines",
  whichBatch: "Which batch?",
  howManyToSend: (unit: string) => `How many ${unit}?`,
  onlyThisMany: (qty: string) => `There are only ${qty}.`,
  addToTransfer: "Add",
  sendNow: "Send",
  transferState: {
    dispatched: "On its way",
    received: "Received",
    discrepancy: "Received short",
    cancelled: "Cancelled",
  },
  fromTo: (from: string, to: string) => `${from} → ${to}`,
  sentBy: (name: string, date: string) => `Sent by ${name} · ${date}`,
  receivedBy: (name: string, date: string) => `Received by ${name} · ${date}`,
  sentQty: (qty: string) => `Sent: ${qty}`,
  arrivedQty: (qty: string) => `Arrived: ${qty}`,
  receivedAs: (name: string) => `Received as ${name}`,
  whichOfYours: "Which of your medicines is this?",
  anotherOfMine: "Another of mine",
  addAsNew: "Add as a new medicine",
  newPricesHint: "Your own selling price for each size.",
  howManyArrived: (unit: string) => `How many ${unit} arrived?`,
  whyFewer: "Why did fewer arrive?",
  shortfall: {
    missing: "Missing",
    damaged: "Damaged",
    other: "Other",
  },
  arrivesHereAs: (qty: string) => `Arrives here as ${qty}`,
  notWholeHere: (unit: string) => `That is not a whole number of ${unit}, which your medicine is counted in.`,
  countIn: "Count in",
  addMedicineLink: "+ Add medicine",
  checkBeforeSending: "Check before sending",
  toBranch: (name: string) => `To ${name}`,
  changeAmount: "Change amount",
  editLine: "Edit",
  notEnoughForAll: (name: string) => `Not enough ${name} here for every branch.`,
  editingTransfer: (name: string) => `Changing what goes to ${name}`,
  cancelTransfer: "Cancel transfer",
  cancelTransferConfirm: "Cancel this transfer? Everything goes back onto this branch's shelf.",
  yesCancelTransfer: "Yes, cancel it",
  editTransfer: "Edit",
  correctSent: "Correct amounts sent",
  correctArrived: "Correct amounts arrived",
  correctWeekHint: "Possible for a week after it was received.",
  changeKind: {
    edited: "Edited",
    cancelled: "Cancelled",
    sent_corrected: "Amount sent corrected",
    arrived_corrected: "Amount arrived corrected",
  },
  changeLine: (name: string, from: string, to: string) => `${name}: ${from} → ${to}`,
  keepTransfer: "No, keep it",
  transferChanges: "Changes",
  noChangesToSave: "Nothing was changed.",
  receivedButton: "Received",
  stockToReceive: (n: number) => `Stock to receive (${n})`,
  transfersNeedInternet: "Transfers need internet. Connect and try again.",

  /* ------------------------------------------------------------ branches */
  workingIn: (name: string) => `Branch: ${name}`,
  switchBranch: "Switch branch",
  switchBranchHint: "Everything you see — medicines, stock, sales, credit — is for the branch you pick.",
  branchesHint: "Each branch is run like a pharmacy of its own: its own medicines, prices, stock, suppliers and customers.",
  addBranch: "Add a branch",
  branchLocation: "Where it is (optional)",
  branchAdded: (name: string) => `${name} is added. It starts empty: switch to it from Home to record its medicines.`,
  youAreHere: "You are working here",
  staffBranches: "Works in",
  staffBranchesHint: "They see and sell only in the branches ticked.",
  owners: "Owners",
  ownersEverywhere: "Owners work in every branch.",
  branchGroup: (name: string, n: number) => `${name} (${n})`,
  inNoBranch: "Not in any branch",
  copy: "Copy",
  copied: "Copied",
  staffLoginMessage: (pharmacy: string, username: string, password: string) =>
    `Your login for ${pharmacy}.\nUsername: ${username}\nPassword: ${password}\nOpen the app and choose "Staff".`,
  atLeastOneBranch: "Tick at least one branch.",
  otherBranchesWrote: "Other branches wrote it:",

  /* ---------------------------------------------------------------- deyn */
  onCredit: "On credit (deyn)",
  /** A shop's credit sale, on its receipt: the day it is to be paid by. */
  payBy: "Pay by",
  balanceOwed: "Balance owed",
  creditCustomerName: "Customer name",
  creditCustomerPhone: "Customer phone",
  creditNeedsCustomer: "Credit needs the customer's name and phone number, to find them later.",
  credits: "Credits",
  owedToYou: "Owed to you",
  debtAge: {
    days30: "0–30 days",
    days60: "31–60 days",
    days90: "61–90 days",
    older: "Over 90 days",
  },
  nobodyOwes: "Nobody owes anything.",
  searchCustomers: "Search by name or phone",
  noCustomerFound: "No customer matches.",
  owes: (amount: string) => `Owes ${amount}`,
  paidUp: "Owes nothing",
  inCredit: (amount: string) => `Paid ahead ${amount}`,
  recordPayment: "Record payment",
  paymentAmount: "Amount paid",
  paymentMoreThanOwed: (amount: string) => `That is more than they owe (${amount}). It is kept as paid ahead.`,
  paymentSaved: "Payment recorded.",
  /* credit added on the Credits page, with no sale (2026-10-18) */
  addCredit: "+ Add credit",
  addCreditTitle: "Add credit",
  creditAmount: "Amount owed",
  creditWhatFor: "What is it for (optional)",
  creditWhatForHint: "For example: old debt from the notebook",
  creditDate: "Date",
  creditNotASale: "Not a sale: no stock moves, and it is not in the sales. When it is paid back, it counts as money in.",
  creditSaved: "Credit recorded.",
  sendStatement: "Send statement",
  statement: "Statement",
  statementFor: (month: string) => `Statement · ${month}`,
  owedAtStart: "Owed at the start",
  owedAtEnd: "Owed at the end",
  nothingThisMonth: "Nothing this month.",
  creditHistory: "Credit sales and payments",
  entryKind: {
    sale: (receipt: string) => `Credit sale · receipt ${receipt}`,
    /** The wallet only on payments recorded before the app stopped asking. */
    payment: (wallet: string | null) => (wallet ? `Payment · ${wallet}` : "Payment"),
    cancelled: (receipt: string) => `Cancelled · receipt ${receipt}`,
    /** Added on the Credits page, with no sale. */
    credit: "Credit added",
    creditChanged: "Credit added · changed",
    creditCancelled: "Credit added · removed",
  },
  givenOnCredit: "Given on credit (deyn)",
  creditPaidBack: "Credit paid back",
  creditPaidBackHint: "Already counted in the money received above.",
  oldCreditInRevenue: (amount: string) => `Includes ${amount} of old credit paid back, with no sale behind it.`,

  /* ------------------------------------------------------------- reports */
  thisWeek: "This week",
  thisMonth: "This month",
  allBranches: "All branches",
  salesTotal: "Sales",
  salesCount: (n: number) => `${n} ${n === 1 ? "sale" : "sales"}`,
  moneyReceived: "Money received",
  noSalesInPeriod: "No sales in this time.",
  profit: "Profit",
  costOfMedicines: "What the medicines cost",
  revenue: "Revenue",
  revenueOf: (amount: string) => `Revenue ${amount}`,
  viewMore: (n: number) => `See all (${n})`,
  mostSoldHint: "By how many were sold.",
  deadStock: "Dead stock",
  deadStockSubtitle: (days: number) => `Unsold products in the last ${days} days`,
  nothingDead: (days: number) => `Every medicine with stock has sold in the last ${days} days.`,
  lastSold: (date: string) => `Last sold ${date}`,
  neverSold: "Never sold",
  staffActivity: "Staff activity",
  staffActivityHint: "Who sold, cancelled, edited or changed stock, and when.",
  everyone: "Everyone",
  noActivity: "Nothing yet.",
  reportsNeedInternet: "Reports need internet. Connect and try again.",
  chooseDates: "Choose dates",
  whichDays: "Which days",
  rangeTooLong: "Choose at most two years at a time.",
  sold: "Sold",
  fromDate: "From",
  toDate: "To",
  reportsFirstTime: "Reports need internet once, to fetch the figures. Connect, and they will be here — with or without internet after that.",
  reportsUpTo: (when: string) => `No internet: figures as at ${when}, with this phone's own sales since.`,
  profitEstimate: "Includes sales not yet sent, costed at each medicine's average cost until they are.",
  deadStockAsOf: (when: string) => `No internet: the list as it was at ${when}.`,
  activity: {
    sale_created: (receipt: string, total: string) => `Sold receipt ${receipt} · ${total}`,
    sale_edited: (receipt: string) => `Edited receipt ${receipt}`,
    sale_cancelled: (receipt: string) => `Cancelled receipt ${receipt}`,
    stock_recorded: (medicine: string, qty: string) => `Recorded stock: ${medicine} · ${qty}`,
    stock_changed: (medicine: string, qty: string) => `Changed stock: ${medicine} · ${qty}`,
    payment_recorded: (customer: string, amount: string, wallet: string | null) =>
      wallet ? `Payment from ${customer} · ${amount} · ${wallet}` : `Payment from ${customer} · ${amount}`,
    cost_corrected: (medicine: string, from: string, to: string, unit: string) =>
      `Corrected cost: ${medicine} · ${from} → ${to} per ${unit}`,
    medicine_added: (medicine: string) => `Added medicine: ${medicine}`,
    medicine_changed: (medicine: string) => `Changed medicine: ${medicine}`,
    medicine_archived: (medicine: string) => `Archived medicine: ${medicine}`,
    refused_sale_removed: (receipt: string) => `Removed a sale not accepted: receipt ${receipt}`,
  },
  soldOfflineAt: (time: string) => `made with no internet at ${time}`,

  /* ----------------------------------------------------- needs attention */
  needsAttention: "Needs attention",
  attentionCount: (n: number) => `${n} to look at`,
  nothingNeedsAttention: "Nothing needs attention.",
  attentionRefused: "Sales not accepted",
  attentionRefusedHint:
    "The customer paid and took the goods, but the sale is not recorded. Put right what stopped it, then Try again — or Remove it.",
  attentionBelowZero: "Medicines below zero",
  attentionBelowZeroHint: "More was sold than was recorded. Record the stock that is on the shelf.",
  attentionExpiredSold: "Expired stock sold, last 7 days",
  refusedBy: (seller: string, date: string) => `${seller} · ${date}`,
  removeRefused: "Remove",
  removeRefusedConfirm: "Remove this sale from the list? It will never be recorded.",
  attentionChanges: "Cancels and edits not made",
  attentionChangesHint:
    "Made on a phone with no internet, but the sale had been changed in the meantime. Choose: make this change to the sale as it is now, or keep the sale as it is.",
  refusedCancel: (receiptNo: string) => `Cancel · Receipt ${receiptNo}`,
  refusedEdit: (receiptNo: string) => `Edit · Receipt ${receiptNo}`,
  makeThisChange: "Make this change",
  keepSaleAsIs: "Keep sale as it is",
  keepSaleConfirm: "Keep the sale as it is? This change will not be made.",
  expiredSoldLine: (qty: string, date: string) => `${qty} · expired ${date}`,

  /* -------------------------------------------------------- out of stock */
  outOfStock: "Out of stock",
  runningLow: "Running low",
  runningLowHint: (n: number) => `Running low means fewer than ${n} left.`,
  nothingOutOfStock: (n: number) => `Every medicine has ${n} or more left.`,

  /* suppliers */
  suppliers: "Suppliers",
  addSupplier: "Add a supplier",
  supplierName: "Supplier name",
  supplierTerms: "Payment terms",
  supplierTermsHint: "Free text, such as \"30 days\" or \"cash on delivery\".",
  noSuppliersYet: "No suppliers yet. They are added when you record stock, or here.",

  /**
   * A count of something, in the right form for this language. English adds
   * an s; the unit is whatever the owner typed, so this cannot be a lookup.
   */
  plural: (word: string, n: number) =>
    n === 1 ? word : /(s|x|z|ch|sh)$/i.test(word) ? `${word}es` : `${word}s`,

  /* ----------------------------------------------------------- greetings */
  signedInAs: (name: string) => `Signed in as ${name}`,
  greeting: (partOfDay: "morning" | "afternoon" | "evening", name: string) =>
    `Good ${partOfDay}, ${name}`,
  /** Monday first, like `weekdaysShort`. */
  weekdays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  longDate: (weekday: string, day: number, month: string) => `${weekday} ${day} ${month}`,

  /* ---------------------------------------------------------------- home */
  todaysSales: "Today's sales",
  medicinesTotal: "Medicines",
  inThisBranch: "In this branch",
  allTimeSales: "Sales",
  allTime: "All time",
  allTimeNeedsInternet: "All-time figures need internet once, to fetch them.",
  recentSales: "Recent sales",
  viewAll: "View all",
  medicinesWord: (n: number): string => (n === 1 ? "medicine" : "medicines"),
  expiryCardHint: "Expired or expiring within 3 months",
  outOfStockCardHint: (n: number) => `Out of stock, or fewer than ${n} left`,

  /**
   * Server error codes, rendered for a human.
   *
   * The API returns a stable `code` alongside an English message. The code is
   * what the client branches on and translates; the server's own message is a
   * fallback for anything not listed here, and a development aid. Anything the
   * user is expected to act on belongs in this table.
   */
  errors: {
    /* --- credentials --- */
    invalid_credentials: "Those details are not correct.",
    account_locked: "This account is locked. Ask the owner to reset your password.",
    temporarily_locked: (minutes: number) =>
      `Too many attempts. Try again in ${minutes} minute${minutes === 1 ? "" : "s"}.`,
    too_many_attempts: (minutes: number) =>
      `Too many attempts. Try again in ${minutes} minute${minutes === 1 ? "" : "s"}.`,
    password_too_short: (min: number) => `Use at least ${min} characters.`,
    password_unchanged: "Choose a password you have not used here before.",
    weak_password: "That password is too easy to guess.",

    /* --- session --- */
    unauthenticated: "Please sign in again.",
    invalid_access_token: "Please sign in again.",
    invalid_refresh_token: "Please sign in again.",
    refresh_token_reused: "Your session was ended for security. Please sign in again.",
    pharmacy_deleted: "This pharmacy was deleted. For 30 days after that, support can bring it back.",
    /* --- which app (FarmashiBook or DukaanBook) --- */
    wrong_app: "This account belongs to DukaanBook. Sign in there.",
    unknown_app: "This app is not known.",
    invalid_product: "Check the product's sizes and prices.",
    missing_request_context: "Something went wrong. Please sign in again.",

    /* --- verification --- */
    invalid_code: "That code is wrong or has expired. Request a new one.",
    invalid_verification_token: "That took too long. Start again.",
    verification_already_used: "That code has already been used. Request a new one.",
    too_many_codes: "Too many codes requested. Wait a few minutes.",
    too_many_sms: (minutes: number) =>
      `Try again in ${minutes} minute${minutes === 1 ? "" : "s"}, or use your email instead. Codes by SMS are limited.`,
    codes_unavailable: "Codes cannot be sent right now. Try again later.",
    email_already_registered: "That email is already in use.",
    contact_phone_required: "Enter the pharmacy's phone number.",
    already_yours: "That is already how you sign in.",
    invalid_target: "Enter a phone number or an email.",
    phone_already_registered: "That number is already in use.",
    invitation_not_found: "That invitation is no longer valid. Ask the owner to send another.",

    /* --- people and permissions --- */
    forbidden: "You do not have access to this.",
    username_taken: "That username is already in use. Try one of the suggestions.",
    staff_limit_reached: "You have used every staff account on your plan.",
    not_main_owner: "Only the main owner can do this.",
    cannot_change_yourself: "You cannot do this to your own account.",
    owner_invitation_pending: "They have not accepted the invitation yet.",
    owner_not_active: "Only an active owner can become the main owner.",
    main_owner_cannot_leave: "Make another owner the main owner first.",
    branch_limit_reached: "Your plan has no room for another branch.",
    plan_expired: "Your plan has ended. You can look at everything, but not add new sales or stock until it is renewed. Your data is safe.",
    cannot_edit_admin: "Owner accounts are not managed here.",
    cannot_reset_admin: "Owners recover their own password by phone.",
    invalid_branch: "That branch is not available.",

    /* --- products and stock --- */
    barcode_taken: "That barcode is already on another product.",
    category_not_found: "That category is no longer there.",
    supplier_not_found: "That supplier is no longer there.",
    unknown_size: "That size is not on this product.",
    size_exists: "This medicine already has a size holding that many.",
    invalid_stock: "Check what you entered for this stock.",
    expired_stock_needs_confirmation: "This stock has already expired.",
    invalid_stock_movement: "That is not a valid quantity.",

    invalid_adjustment: "Check the new number and the reason.",
    count_needs_stock: "There is no stock of it yet. Record what arrived on Add product.",
    invalid_sale: "Check the prices and the payments.",
    sale_changed: "Someone else changed this sale. Open it again.",
    sale_cancelled: "This sale has been cancelled.",
    sale_has_credit: "That sale already has credit on it. Change it from the sale instead.",
    credit_more_than_sale: "The credit cannot be more than what was paid for that sale.",
    link_too_late: "Credit can only be added to a sale from the last 30 days.",
    cancel_too_late: "A sale can only be cancelled for 14 days after it was made.",
    edit_too_late: "A sale can only be edited for 30 days after it was made.",
    note_required: "Say what happened.",

    /* --- transfers --- */
    same_branch: "Pick another branch: this is the branch you are in.",
    already_received: "This transfer has already been received.",
    not_received: "This transfer has not been received yet.",
    correct_too_late: "Amounts can only be corrected for a week after receipt.",
    less_than_arrived: "It cannot be less than what arrived.",
    already_used: "Some of it has already been sold or moved here.",
    not_whole_units: "That does not make whole units of your medicine.",
    different_unit: "That medicine is counted in a unit that does not match.",
    every_line: "Say something for every medicine.",
    invalid_receipt: "Check what arrived and the reasons.",
    base_size_needed: "The smallest size needs a price.",

    /* --- catch-alls --- */
    validation_failed: "Check the details you entered.",
    not_found: "That is not here.",
    too_many_requests: "The server is busy. Try again in a moment.",
    took_too_long: "That took too long. Try again.",
    report_too_long: "That took too long. Choose fewer days and try again.",
    network: "Could not reach the server. Check your connection.",
    timeout: "The server took too long to answer.",
    unknown: "Something went wrong.",
  },
};

/**
 * Deliberately NOT `as const`.
 *
 * With literal types, `typeof en` would demand that Somali use the same literal
 * strings — "Pharmacy" and nothing else — which is precisely backwards. Widened
 * to `string`, the type still requires every key with a compatible shape, and
 * that is the check worth having.
 */
export default en;
